/**
 * First-visit guest tour (TMMAPP-0090). Step 1 is one dialog over a shared virtual account. Step 2 is
 * a slim footer in the main column. Done writes `onboardingCompleted` and returns focus to the
 * page's h1; Esc writes `onboardingDismissed` (a backdrop click does nothing). A failed PATCH
 * closes the tour and retries once.
 */
import { useId, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { patchMe } from '@/api/settings';
import { SubmitButton } from '@/components/SubmitButton';
import { useSubscribeDialogOpen } from '@/features/subscribe/subscribeOfferContext';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { SIDEBAR_WIDTH } from '@/shell/sidebarPaper';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { personName } from '@/i18n/personName';
import { resolveTokens } from '@/theme/theme';
import { sponsorName } from './sponsorName';

const BOUNCE = {
  '0%, 100%': { transform: 'translateY(0)' },
  '50%': { transform: 'translateY(-6px)' },
};
const BOUNCE_KEYFRAMES = {
  '@keyframes tmmWizardBounceA': BOUNCE,
  '@keyframes tmmWizardBounceB': BOUNCE,
};

type Phase = 'welcome' | 'footer' | 'closed';

/**
 * The tour's step dots (G:671-674): the current step is a 22px `brandBoundary` pill (the
 * served non-text brand role), earlier steps `brandSoft`, later steps `line`. Decorative: the
 * step line beside them says the same in words.
 */
function TourDots({ current, total }: { current: number; total: number }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box aria-hidden sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
      {Array.from({ length: total }, (_, index) => (
        <Box
          key={index}
          data-step-dot={index === current ? 'current' : index < current ? 'done' : 'next'}
          sx={{
            width: index === current ? 22 : 8,
            height: 8,
            borderRadius: 4,
            bgcolor:
              index === current
                ? tokens.brandBoundary
                : index < current
                  ? tokens.brandSoft
                  : tokens.line,
          }}
        />
      ))}
    </Box>
  );
}

function useBounceGeneration(stepKey: string): number {
  const [seen, setSeen] = useState(stepKey);
  const [generation, setGeneration] = useState(1);
  if (seen !== stepKey) {
    setSeen(stepKey);
    setGeneration((value) => value + 1);
  }
  return generation;
}

/** Border box, so wrap and the safe-area padding are both in the spacer. */
function measuredFooterHeight(element: HTMLElement, entry?: ResizeObserverEntry): number {
  const block = entry?.borderBoxSize?.[0]?.blockSize ?? 0;
  const height = block > 0 ? block : element.getBoundingClientRect().height;
  return Math.ceil(height);
}

function onboardingSettled(
  onboarding: { completedAt?: string | null; dismissedAt?: string | null } | null,
): boolean {
  return (
    onboarding !== null &&
    (typeof onboarding.completedAt === 'string' || typeof onboarding.dismissedAt === 'string')
  );
}

export function GuestTour() {
  const { t } = useTranslation();
  const me = useMe();
  const refetch = useMeRefetch();
  const location = useLocation();
  const theme = useTheme();
  const fullScreen = useDialogFullScreen();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [phase, setPhase] = useState<Phase>('welcome');
  const settled = useRef(false);
  const footerRef = useRef<HTMLElement | null>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const titleId = useId();
  // An invitation just accepted opens the subscribe offer first; the tour starts once it closes.
  const offerOpen = useSubscribeDialogOpen();

  const match = /^\/v\/(\d+)$/.exec(location.pathname);
  const virtualAccountId = match ? Number(match[1]) : null;
  const row =
    virtualAccountId === null
      ? undefined
      : me.virtualAccounts.find((virtualAccount) => virtualAccount.id === virtualAccountId);
  const eligible =
    me.tier === 'GUEST' &&
    row !== undefined &&
    !row.mine &&
    !onboardingSettled(me.onboarding) &&
    phase !== 'closed' &&
    !offerOpen;
  const showWelcome = eligible && phase === 'welcome';
  const showFooter = eligible && phase === 'footer';
  const generation = useBounceGeneration(showFooter ? 'footer' : 'welcome');
  const virtualAccount = row?.name ?? '';
  const sponsor = sponsorName(me, virtualAccountId) || personName(null, t);

  useLayoutEffect(() => {
    if (!showFooter) return;
    const element = footerRef.current;
    if (element === null) return;
    const apply = (entry?: ResizeObserverEntry) => {
      const next = measuredFooterHeight(element, entry);
      setFooterHeight((prev) => (prev === next ? prev : next));
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) apply(entry);
    });
    observer.observe(element, { box: 'border-box' });
    return () => observer.disconnect();
  }, [showFooter]);

  if (!showWelcome && !showFooter) return null;

  const bounceName = generation % 2 === 0 ? 'tmmWizardBounceB' : 'tmmWizardBounceA';
  const bounceSx = {
    ...BOUNCE_KEYFRAMES,
    animation: reduceMotion ? 'none' : `${bounceName} 0.9s ease-in-out 3`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  };

  async function writeOnboarding(body: {
    onboardingCompleted?: boolean;
    onboardingDismissed?: boolean;
  }) {
    try {
      await patchMe(body);
    } catch {
      try {
        await patchMe(body);
      } catch {
        // Already closed. A second failure does not put the dialog back.
        return;
      }
    }
    try {
      await refetch();
    } catch {
      // The tour stays closed for this visit even if /me cannot be reread.
    }
  }

  function dismiss() {
    if (settled.current) return;
    settled.current = true;
    setPhase('closed');
    void writeOnboarding({ onboardingDismissed: true });
  }

  function done(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (settled.current) return;
    settled.current = true;
    setPhase('closed');
    // Focus goes back to the page, not to the body the footer leaves behind (TMMAPP-0097 B9).
    document.querySelector<HTMLElement>('main h1')?.focus();
    void writeOnboarding({ onboardingCompleted: true });
  }

  const bold = <Box component="strong" sx={{ color: 'text.primary', fontWeight: 500 }} />;

  return (
    <>
      {showWelcome && (
        <Dialog
          open
          onClose={(_event, reason) => {
            // A stray click outside is not a decision: only Esc dismisses (TMMAPP-0097 B9).
            if (reason === 'escapeKeyDown') dismiss();
          }}
          fullWidth
          maxWidth="sm"
          fullScreen={fullScreen}
          disableAutoFocus
          aria-labelledby={titleId}
          slotProps={{
            paper: { sx: { maxWidth: 480 } },
            transition: {
              onEntered: () => {
                document.getElementById(titleId)?.focus();
              },
            },
          }}
        >
          <Box sx={{ px: 3, pt: 2 }}>
            <Typography variant="overline" component="p" sx={{ color: 'text.secondary' }}>
              {t('guest.invitedEyebrow')}
            </Typography>
          </Box>
          <DialogTitle id={titleId} tabIndex={-1} component="h2" sx={{ pt: 0.5 }}>
            {t('guest.welcomeTitle', { virtualAccount })}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Typography variant="body2" component="p" sx={{ color: 'text.secondary' }}>
                <Trans i18nKey="guest.welcomeBody1" values={{ sponsor }} components={{ bold }} />
              </Typography>
              <Typography variant="body2" component="p" sx={{ color: 'text.secondary' }}>
                {t('guest.welcomeBody2', { sponsor })}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <SubmitButton type="button" onClick={() => setPhase('footer')} sx={bounceSx}>
              {t('guest.lookAround')}
            </SubmitButton>
          </DialogActions>
        </Dialog>
      )}
      {showFooter && (
        <>
          <Box aria-hidden sx={{ flexShrink: 0 }} style={{ height: footerHeight }} />
          <Box
            ref={footerRef}
            component="section"
            role="region"
            aria-label={t('guest.tourRegion', { virtualAccount })}
            sx={{
              position: 'fixed',
              zIndex: theme.zIndex.appBar,
              insetInlineStart: { xs: 0, lg: `${SIDEBAR_WIDTH}px` },
              insetInlineEnd: 0,
              bottom: 0,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              px: { xs: 2, md: 3 },
              pt: 1.5,
              pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              bgcolor: (muiTheme) => resolveTokens(muiTheme).surface,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <TourDots current={1} total={2} />
            <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 0 }}>
              {t('guest.tourStep', { n: 2, total: 2, virtualAccount })}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Link
              component={RouterLink}
              to="/me/own-books"
              state={{ from: `${location.pathname}${location.search}` }}
              variant="body2"
              color="text.secondary"
            >
              {t('guest.ownBooksLink')}
            </Link>
            <SubmitButton type="button" onClick={done}>
              {t('guest.done')}
            </SubmitButton>
          </Box>
        </>
      )}
    </>
  );
}
